import React, { useState } from 'react';
import { Card, Form, Button, Alert, Row, Col, Badge } from 'react-bootstrap';
import { HostInfo, AddTokensRequest } from '../../types';
import gameService from '../../services/gameService';

interface TokenManagementProps {
  hostInfo: HostInfo | undefined;
  onTokenPurchase: (updatedHostInfo: HostInfo) => void;
}

const TokenManagement: React.FC<TokenManagementProps> = ({ hostInfo, onTokenPurchase }) => {
  const [tokenAmount, setTokenAmount] = useState<number>(10);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const handleTokenPurchase = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      if (tokenAmount <= 0) {
        setError('Please select a valid token amount');
        setLoading(false);
        return;
      }

      const request: AddTokensRequest = {
        tokenAmount
      };

      const response = await gameService.addTokens(request);

      if (response.success && response.data) {
        setSuccess(`Successfully purchased ${tokenAmount} tokens!`);
        onTokenPurchase(response.data);
        setTokenAmount(10); // Reset to default
      } else {
        setError(response.message || 'Failed to purchase tokens');
      }
    } catch (err) {
      setError('An unexpected error occurred while purchasing tokens');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="mb-4">
      <Card.Header>
        <h4>Token Management</h4>
      </Card.Header>
      <Card.Body>
        <Row className="mb-4">
          <Col>
            <h5>Current Token Balance: <Badge bg="primary" pill>{hostInfo?.tokenBalance || 0}</Badge></h5>
            <p className="text-muted">Tokens are required to create game sessions. Each game session costs 1 token.</p>
          </Col>
        </Row>

        {error && <Alert variant="danger">{error}</Alert>}
        {success && <Alert variant="success">{success}</Alert>}

        <Form onSubmit={handleTokenPurchase}>
          <Form.Group className="mb-3">
            <Form.Label>Purchase Tokens</Form.Label>
            <Row>
              <Col md={6}>
                <Form.Control
                  type="number"
                  min="1"
                  max="100"
                  value={tokenAmount}
                  onChange={(e) => setTokenAmount(parseInt(e.target.value) || 0)}
                />
                <Form.Text className="text-muted">
                  Select the number of tokens you wish to purchase (1-100)
                </Form.Text>
              </Col>
              <Col md={6}>
                <Button 
                  variant="success" 
                  type="submit" 
                  disabled={loading || tokenAmount <= 0}
                  className="w-100"
                >
                  {loading ? 'Processing...' : `Purchase ${tokenAmount} Tokens`}
                </Button>
              </Col>
            </Row>
          </Form.Group>
        </Form>
      </Card.Body>
    </Card>
  );
};

export default TokenManagement;
